import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFilter, FiX, FiGrid, FiList, FiChevronDown, FiSearch } from 'react-icons/fi';
import { getProducts, getCollections } from '../firebase/database';
import ProductCard from '../components/shop/ProductCard';
import SEO from '../components/common/SEO';
import { SkeletonCard } from '../components/common/Loader';
import { formatPrice } from '../utils/helpers';
import ContactCTA from '../components/common/ContactCTA';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');

  // Filters
  const [selectedCategories, setSelectedCategories] = useState(
    searchParams.get('category') ? [searchParams.get('category')] : []
  );
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    getCollections().then(setCollections).catch(console.error);
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategories, priceRange, inStockOnly, sortBy]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (selectedCategories.length === 1) filters.category = selectedCategories[0];
      if (inStockOnly) filters.inStock = true;

      let sort = 'createdAt';
      let dir = 'desc';
      if (sortBy === 'price-asc') { sort = 'price'; dir = 'asc'; }
      if (sortBy === 'price-desc') { sort = 'price'; dir = 'desc'; }
      if (sortBy === 'rating') { sort = 'rating'; dir = 'desc'; }
      filters.orderByField = sort;
      filters.orderDir = dir;

      let prods = await getProducts(filters);

      // Client-side filtering
      if (selectedCategories.length > 1) {
        prods = prods.filter(p => selectedCategories.includes(p.category));
      }
      prods = prods.filter(p => {
        const price = p.salePrice || p.price;
        return price >= priceRange[0] && price <= priceRange[1];
      });

      setProducts(prods);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = search
    ? products.filter(p =>
        p.title?.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase()) ||
        p.category?.toLowerCase().includes(search.toLowerCase())
      )
    : products;

  const toggleCategory = (catId) => {
    setSelectedCategories(prev =>
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'price-asc', label: 'Price: Low to High' },
    { value: 'price-desc', label: 'Price: High to Low' },
    { value: 'rating', label: 'Top Rated' },
  ];

  return (
    <>
      <SEO
        title="Shop All Furniture"
        description="Browse our full collection of premium chairs, tables, and furniture. Filter by category, price, and more."
        keywords="buy chairs online pakistan, furniture shop, plastic chairs, office chairs"
      />

      {/* Page Header */}
      <div className="bg-[#0f2347] py-12 relative overflow-hidden">
        <div className="absolute inset-0 diagonal-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-2">Our Collection</p>
            <h1 className="font-display text-4xl font-black text-white">Shop All Products</h1>
            <p className="text-white/60 text-sm mt-2">{filteredProducts.length} products found</p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border transition-colors ${
                filterOpen ? 'bg-[#1a3a6e] text-white border-[#1a3a6e]' : 'border-gray-300 hover:border-[#1a3a6e] hover:text-[#1a3a6e]'
              }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {selectedCategories.length > 0 && (
                <span className="bg-[#e63946] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {selectedCategories.length}
                </span>
              )}
            </button>

            {/* Active filters */}
            {selectedCategories.map(catId => {
              const cat = collections.find(c => c.id === catId || c.slug === catId);
              return (
                <span key={catId} className="flex items-center gap-1.5 bg-[#1a3a6e]/10 text-[#1a3a6e] text-xs px-3 py-1.5 font-medium">
                  {cat?.name || catId}
                  <button onClick={() => toggleCategory(catId)}><FiX className="w-3 h-3" /></button>
                </span>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative hidden sm:block">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-200 text-sm outline-none focus:border-[#1a3a6e] w-48"
              />
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="appearance-none border border-gray-200 px-4 py-2 pr-8 text-sm outline-none focus:border-[#1a3a6e] cursor-pointer"
              >
                {sortOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* View toggle */}
            <div className="flex border border-gray-200">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-[#1a3a6e] text-white' : 'text-gray-500 hover:text-[#1a3a6e]'}`}
              >
                <FiGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-[#1a3a6e] text-white' : 'text-gray-500 hover:text-[#1a3a6e]'}`}
              >
                <FiList className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <AnimatePresence>
            {filterOpen && (
              <motion.aside
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 260, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex-shrink-0 overflow-hidden"
              >
                <div className="w-64 space-y-6">
                  {/* Categories */}
                  <div>
                    <h3 className="font-bold text-sm text-gray-900 uppercase tracking-wider mb-3">Categories</h3>
                    <div className="space-y-2">
                      {collections.map(cat => (
                        <label key={cat.id} className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(cat.id) || selectedCategories.includes(cat.slug)}
                            onChange={() => toggleCategory(cat.id)}
                            className="custom-check"
                          />
                          <span className="text-sm text-gray-700 group-hover:text-[#1a3a6e] transition-colors">
                            {cat.name}
                          </span>
                        </label>
                      ))}
                      {collections.length === 0 && (
                        ['Plastic Chairs', 'Office Chairs', 'Baby Chairs', 'Tables', 'Dining Sets', 'Outdoor'].map(c => (
                          <label key={c} className="flex items-center gap-3 cursor-pointer group">
                            <input type="checkbox" className="custom-check" onChange={() => toggleCategory(c.toLowerCase().replace(' ', '-'))} />
                            <span className="text-sm text-gray-700 group-hover:text-[#1a3a6e] transition-colors">{c}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Price Range */}
                  <div>
                    <h3 className="font-bold text-sm text-gray-900 uppercase tracking-wider mb-3">Price Range</h3>
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="number"
                          placeholder="Min"
                          value={priceRange[0]}
                          onChange={e => setPriceRange([+e.target.value, priceRange[1]])}
                          className="w-full border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#1a3a6e]"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={priceRange[1]}
                          onChange={e => setPriceRange([priceRange[0], +e.target.value])}
                          className="w-full border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#1a3a6e]"
                        />
                      </div>
                      <p className="text-xs text-gray-500">{formatPrice(priceRange[0])} – {formatPrice(priceRange[1])}</p>
                    </div>
                  </div>

                  {/* In Stock */}
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={inStockOnly}
                        onChange={e => setInStockOnly(e.target.checked)}
                        className="custom-check"
                      />
                      <span className="text-sm font-medium text-gray-700">In Stock Only</span>
                    </label>
                  </div>

                  <button
                    onClick={() => { setSelectedCategories([]); setPriceRange([0, 100000]); setInStockOnly(false); }}
                    className="text-sm text-[#e63946] hover:underline"
                  >
                    Clear All Filters
                  </button>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>

          {/* Products Grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className={`grid gap-5 ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
                {Array(12).fill(0).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-24">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiSearch className="w-8 h-8 text-gray-300" />
                </div>
                <h3 className="font-display text-2xl font-bold text-gray-800 mb-2">No products found</h3>
                <p className="text-gray-500">Try adjusting your filters or search terms</p>
              </div>
            ) : (
              <div className={`grid gap-5 ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2'}`}>
                {filteredProducts.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <ContactCTA />
    </>
  );
};

export default ShopPage;
